// PfmeaEditorHelpers.tsx - Extracted helper components to reduce cyclomatic complexity
// ============================================================

import React from 'react';
import clsx from 'clsx';

// 4M Categories with colors
export const PROCESS_ELEMENTS = [
    { value: 'Man', label: 'Man', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
    { value: 'Machine', label: 'Machine', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
    { value: 'Material', label: 'Material', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300' },
    { value: 'Method', label: 'Method', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
];

// Status badges
export const STATUS_STYLES: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    review: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    approved: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
};

// Editable cell types
export type EditableField = 'processStepNumber' | 'processStepName' | 'processFunction' | 'processElement' |
    'failureMode' | 'failureEffectEnd' | 'failureCause' | 'severity' | 'occurrence' | 'detection' |
    'preventionControls' | 'detectionControls' | 'specialCharacteristic';

// ============================================================================
// CELL INPUT COMPONENTS - Separated by field type to reduce complexity
// ============================================================================

interface CellInputProps {
    value: any;
    onChange: (value: any) => void;
    onBlur: () => void;
}

export const ProcessElementSelect: React.FC<CellInputProps> = ({ value, onChange, onBlur }) => (
    <select
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:border-slate-600"
    >
        {PROCESS_ELEMENTS.map(el => (
            <option key={el.value} value={el.value}>{el.label}</option>
        ))}
    </select>
);

export const SpecialCharacteristicSelect: React.FC<CellInputProps> = ({ value, onChange, onBlur }) => (
    <select
        autoFocus
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        onBlur={onBlur}
        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:border-slate-600"
    >
        <option value="">—</option>
        <option value="CC">CC (Critical)</option>
        <option value="SC">SC (Significant)</option>
    </select>
);

export const RatingInput: React.FC<CellInputProps> = ({ value, onChange, onBlur }) => (
    <input
        type="number"
        autoFocus
        min={1}
        max={10}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value) || 1)}
        onBlur={onBlur}
        className="w-16 px-2 py-1 text-sm text-center border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:border-slate-600"
    />
);

export const ControlsInput: React.FC<CellInputProps> = ({ value, onChange, onBlur }) => (
    <input
        type="text"
        autoFocus
        value={Array.isArray(value) ? value.join('; ') : value}
        onChange={(e) => onChange(e.target.value.split(';').map(s => s.trim()).filter(Boolean))}
        onBlur={onBlur}
        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:border-slate-600"
        placeholder="Enter controls; separated by semicolon"
    />
);

export const TextInput: React.FC<CellInputProps> = ({ value, onChange, onBlur }) => (
    <input
        type="text"
        autoFocus
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:border-slate-600"
    />
);

// ============================================================================
// CELL DISPLAY COMPONENTS - Separated by field type
// ============================================================================

interface CellDisplayProps {
    value: any;
}

export const ProcessElementDisplay: React.FC<CellDisplayProps> = ({ value }) => {
    const el = PROCESS_ELEMENTS.find(e => e.value === value);
    return (
        <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', el?.color)}>
            {value}
        </span>
    );
};

export const SpecialCharacteristicDisplay: React.FC<CellDisplayProps> = ({ value }) => {
    if (value === 'CC') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500 text-white">CC</span>;
    if (value === 'SC') return <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-500 text-white">SC</span>;
    return <span className="text-slate-400">—</span>;
};

export const RatingDisplay: React.FC<CellDisplayProps> = ({ value }) => (
    <span className="font-mono font-bold">{value}</span>
);

export const ControlsDisplay: React.FC<CellDisplayProps> = ({ value }) => {
    if (Array.isArray(value) && value.length > 0) {
        return (
            <span className="text-sm block truncate" title={value.join(', ')}>
                {value[0]}
                {value.length > 1 && <span className="text-slate-400"> +{value.length - 1}</span>}
            </span>
        );
    }
    return <span className="text-slate-400">—</span>;
};

export const TextDisplay: React.FC<CellDisplayProps> = ({ value }) => (
    value ? <span className="block truncate" title={String(value)}>{value}</span> : <span className="text-slate-400">—</span>
);

// ============================================================================
// GET INPUT COMPONENT BY FIELD TYPE (Strategy Pattern)
// ============================================================================

export const getInputComponent = (field: EditableField) => {
    const inputMap: Record<string, React.FC<CellInputProps>> = {
        processElement: ProcessElementSelect,
        specialCharacteristic: SpecialCharacteristicSelect,
        severity: RatingInput,
        occurrence: RatingInput,
        detection: RatingInput,
        preventionControls: ControlsInput,
        detectionControls: ControlsInput,
    };
    return inputMap[field] || TextInput;
};

export const getDisplayComponent = (field: EditableField) => {
    const displayMap: Record<string, React.FC<CellDisplayProps>> = {
        processElement: ProcessElementDisplay,
        specialCharacteristic: SpecialCharacteristicDisplay,
        severity: RatingDisplay,
        occurrence: RatingDisplay,
        detection: RatingDisplay,
        preventionControls: ControlsDisplay,
        detectionControls: ControlsDisplay,
    };
    return displayMap[field] || TextDisplay;
};

// ============================================================================
// FILTER UTILITIES
// ============================================================================

import { PfmeaItem } from '../types';

export interface FilterState {
    viewMode: 'all' | 'draft' | 'review' | 'approved';
    filter4M: 'all' | 'Man' | 'Machine' | 'Material' | 'Method';
    filterPriority: 'all' | 'H' | 'M' | 'L';
    filterSC: 'all' | 'CC' | 'SC' | 'none';
    searchTerm: string;
}

export const applyFilters = (items: PfmeaItem[], filters: FilterState): PfmeaItem[] => {
    let result = [...items];

    // Status filter
    if (filters.viewMode !== 'all') {
        result = result.filter(item => item.status === filters.viewMode);
    }

    // 4M filter
    if (filters.filter4M !== 'all') {
        result = result.filter(item => item.processElement === filters.filter4M);
    }

    // Priority filter
    if (filters.filterPriority !== 'all') {
        result = result.filter(item => item.actionPriority === filters.filterPriority);
    }

    // SC/CC Classification filter
    if (filters.filterSC && filters.filterSC !== 'all') {
        if (filters.filterSC === 'none') {
            result = result.filter(item => !item.specialCharacteristic || item.specialCharacteristic === 'none');
        } else {
            result = result.filter(item => item.specialCharacteristic === filters.filterSC);
        }
    }

    // Search
    if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        result = result.filter(item =>
            item.processStepName?.toLowerCase().includes(term) ||
            item.failureMode?.toLowerCase().includes(term) ||
            item.failureCause?.toLowerCase().includes(term) ||
            item.failureEffectEnd?.toLowerCase().includes(term) ||
            item.failureEffect?.toLowerCase().includes(term)
        );
    }

    return result;
};

export const sortItems = <T extends PfmeaItem>(
    items: T[],
    sortField: keyof T,
    sortDirection: 'asc' | 'desc'
): T[] => {
    return [...items].sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        if (aVal === undefined || bVal === undefined) return 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sortDirection === 'asc'
                ? aVal.localeCompare(bVal)
                : bVal.localeCompare(aVal);
        }
        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }
        return 0;
    });
};

export const calculateStatusCounts = (items: PfmeaItem[]) => ({
    all: items.length,
    draft: items.filter(i => i.status === 'draft').length,
    review: items.filter(i => i.status === 'review').length,
    approved: items.filter(i => i.status === 'approved').length,
});

// ============================================================================
// TRANSFORM UTILITIES
// ============================================================================

import { calculateActionPriority } from '../services/pfmeaService';

/**
 * Transform a raw item from context to a properly typed PfmeaItem
 * Uses default values for all missing fields
 */
export const transformContextItem = (
    item: any,
    activeProjectName: string
): PfmeaItem => ({
    id: item.id || `pfmea-${Date.now()}`,
    projectName: item.projectName || activeProjectName || '',
    epochId: item.epochId,
    standard: item.standard || 'AIAG_VDA',
    processStepNumber: item.processStepNumber || '',
    processStepName: item.processStepName || '',
    processFunction: item.processFunction || '',
    processElement: item.processElement || 'Method',
    failureMode: item.failureMode || '',
    failureEffectEnd: item.failureEffectEnd || item.failureEffect || '',
    failureEffect: item.failureEffect || '',
    failureCause: item.failureCause || '',
    severity: item.severity || 1,
    occurrence: item.occurrence || 1,
    detection: item.detection || 1,
    actionPriority: item.actionPriority || calculateActionPriority(item.severity || 1, item.occurrence || 1, item.detection || 1),
    preventionControls: item.preventionControls || [],
    detectionControls: item.detectionControls || [],
    specialCharacteristic: (item.specialCharacteristic === null || item.specialCharacteristic === undefined) ? 'none' : item.specialCharacteristic,
    recommendedActions: item.recommendedActions || [],
    status: item.status || 'draft',
    createdBy: item.createdBy || '',
    createdAt: item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedAt || item.modifiedAt || new Date().toISOString(),
    changeHistory: item.changeHistory || [],
});

/**
 * Transform an array of context items to PfmeaItems
 */
export const transformContextItems = (
    contextItems: any[],
    activeProjectName: string
): PfmeaItem[] => {
    return contextItems.map(item => transformContextItem(item, activeProjectName));
};
